// Story 8.13 (ADO-2022), D-8: what the cart's Fulfilment card may offer.
//
//   const options = useFulfilmentOptions(cart, cutOffMark(cutOff.status));
//   // FulfilmentOptions | null
//
// The options belong to the cart's Trading Account (Story 8.4, ADO-2013), so they are read for
// the cart (the depot AND the account; staff name none) and cached per cart (`cartTargetKey`).
//
// Behaviour:
//   - One read per cart: the answer is cached per cart in a small store, so every consumer, a
//     remount and a switch back to the cart reuse it. Two consumers mounting together share one
//     request.
//   - An answer expires at the next half hour after its read started (UTC :00 and :30, which
//     are Europe/Dublin's too, midnight included): today's first open slot and today itself
//     (the collection calendar's lower bound, D-4) move only then. An expired answer is read
//     again on mount, and a mounted consumer reads it again at expiry; it stays shown until the
//     new one lands. The server still re-checks every send (D-6).
//   - A failed read of an expired (or missing) answer is retried while a consumer is mounted:
//     30 s, then 60 s, then every 2 min, never later than the next half hour after the failure.
//     A tab coming back into view (`visibilitychange`) or the device coming back online reads an
//     expired answer again at once. An unmount or a switch of cart stops both; a read that
//     succeeds ends the retries, and its answer expires at the half hour as usual.
//   - It returns `null` ("unknown") while the cart's first answer loads, when the read fails, and
//     with no cart. A failed read is not cached, so a later mount reads again.
//   - Answers are keyed by the cart they were read for, and the hook returns only the current
//     cart's, so a slow answer for cart A never paints on cart B.
//   - The dates on offer move when the cart's cut-off passes (D-2), so the caller passes the
//     cut-off's mark ({@link cutOffMark}). The latest mark asked for is tracked per cart: an answer
//     is cached under the mark its read started under, and when that is not the latest (the mark
//     moved, or first arrived, while the read was in flight or after it), the options are read
//     again. A mark is never adopted without a read: a read made before the mark was known may
//     predate a cut-off that passed before it landed. The previous answer stays shown until the
//     new one lands (the card does not blink).
//   - A refused choice re-reads the cart's options ({@link refreshFulfilmentOptions}, D-2: the
//     calendar then refreshes), keeping the answer shown meanwhile, or dropping it when the cart
//     no longer offers the choice.
//   - {@link fulfilmentOfferedFor} says whether a send of the cart may carry fulfilment fields:
//     only while its cached answer offers them, so the send matches what the card shows (D-7,
//     D-15).
//   - {@link offeredDeliveryDatesFor} and {@link subscribeToFulfilmentOptions} let the send store
//     seed the requested delivery date the cart kept from its latest send only while that date is
//     still on offer (Enabler E8-G, ADO-3109, AC16).

import { useEffect, useRef } from 'react';
import { create } from 'zustand';
import {
  getFulfilmentOptions,
  type CutOffStatus,
  type FulfilmentOptions,
} from '../api/basket.client';
import { cartTargetKey, type CartTarget } from '../api/cart-target';

interface CachedOptions {
  readonly options: FulfilmentOptions;
  /** The cut-off mark the options were read under; null when unknown. */
  readonly mark: string | null;
  /** The refresh the options were read after ({@link refreshFulfilmentOptions}). */
  readonly refresh: number;
  /** When the options expire (epoch ms): the next half hour after their read started. */
  readonly expiresAt: number;
}

/** The reads that failed in a row for a cart, and when the last one failed (epoch ms). */
interface FailedReads {
  readonly count: number;
  readonly at: number;
}

interface FulfilmentOptionsState {
  /** The answer per cart key (`cartTargetKey`). */
  readonly byCart: Readonly<Record<string, CachedOptions>>;
  /** The failed reads per cart key since its last successful read. */
  readonly failedByCart: Readonly<Record<string, FailedReads>>;
}

const useFulfilmentOptionsStore = create<FulfilmentOptionsState>(() => ({
  byCart: {},
  failedByCart: {},
}));

/** What a cart's options should be read under: the latest mark asked for, and the latest
 *  refresh. */
interface Wanted {
  readonly cart: CartTarget;
  readonly mark: string | null;
  readonly refresh: number;
}

/** The latest wanted read per cart key. */
const wantedByCart = new Map<string, Wanted>();
/** The read in flight per cart key, so consumers mounting together share one request. */
const inFlight = new Set<string>();
/** Bumped by every reset: a read that resolves after it caches nothing and reads no more. */
let generation = 0;

/**
 * The cut-off mark the options follow: the deadline an OPEN cart is counting towards, or the
 * one a PASSED cart has just passed (prefixed, so the flip from OPEN to PASSED is a move). Null
 * with no cut-off (NONE) or none known yet.
 */
export function cutOffMark(status: CutOffStatus | null): string | null {
  if (status === null || status.state === 'NONE') return null;
  return status.state === 'OPEN' ? status.deadline : `passed:${status.passedDeadline}`;
}

const HALF_HOUR_MS = 30 * 60 * 1000;

/** The next half hour after `epochMs` (UTC :00 or :30; Europe/Dublin's offset is whole hours). */
function nextHalfHour(epochMs: number): number {
  return (Math.floor(epochMs / HALF_HOUR_MS) + 1) * HALF_HOUR_MS;
}

/** The waits before each retry of a failed read: 30 s, 60 s, then 2 min from then on. */
const RETRY_BACKOFF_MS = [30_000, 60_000, 120_000] as const;

/** When to retry after `failed`: its backoff, but never later than the next half hour. */
function retryAt(failed: FailedReads): number {
  const wait = RETRY_BACKOFF_MS[Math.min(failed.count, RETRY_BACKOFF_MS.length) - 1] ?? 120_000;
  return Math.min(failed.at + wait, nextHalfHour(failed.at));
}

/** `record` without `key`. */
function without<T>(record: Readonly<Record<string, T>>, key: string): Record<string, T> {
  return Object.fromEntries(Object.entries(record).filter(([entry]) => entry !== key));
}

/** True when the cached answer is what `wanted` asks for and has not expired. */
function isCurrent(cached: CachedOptions | undefined, wanted: Wanted): boolean {
  return (
    cached !== undefined &&
    cached.mark === wanted.mark &&
    cached.refresh === wanted.refresh &&
    Date.now() < cached.expiresAt
  );
}

/** Read the cart's options as wanted, unless a read is in flight (it reads again as it lands
 *  when the wanted read moved meanwhile). A failed read caches nothing. */
function readOptions(cartKey: string): void {
  const wanted = wantedByCart.get(cartKey);
  if (wanted === undefined || inFlight.has(cartKey)) return;
  inFlight.add(cartKey);
  const startedIn = generation;
  // From the read's start: the server answers for the clock when the request arrives.
  const expiresAt = nextHalfHour(Date.now());
  void getFulfilmentOptions(wanted.cart)
    .then(
      (options) => {
        if (startedIn !== generation) return;
        // A read that predates a refresh may only replace an answer still shown: once a refresh
        // dropped the cart's answer (it no longer offers the choice), this one is stale.
        const latest = wantedByCart.get(cartKey);
        const predatesRefresh = latest !== undefined && latest.refresh !== wanted.refresh;
        if (predatesRefresh && useFulfilmentOptionsStore.getState().byCart[cartKey] === undefined) {
          return;
        }
        useFulfilmentOptionsStore.setState((state) => ({
          byCart: {
            ...state.byCart,
            [cartKey]: { options, mark: wanted.mark, refresh: wanted.refresh, expiresAt },
          },
          failedByCart: without(state.failedByCart, cartKey),
        }));
      },
      () => {
        // Unknown: not cached. Counted, so a mounted consumer retries (see the hook).
        if (startedIn !== generation) return;
        useFulfilmentOptionsStore.setState((state) => {
          // The backoff counts the failures since the answer expired (or went missing): one
          // while it was still current (a refresh) starts no count.
          const previous = state.failedByCart[cartKey];
          const expiresAt = state.byCart[cartKey]?.expiresAt ?? 0;
          const count = previous !== undefined && previous.at >= expiresAt ? previous.count + 1 : 1;
          return { failedByCart: { ...state.failedByCart, [cartKey]: { count, at: Date.now() } } };
        });
      },
    )
    .finally(() => {
      if (startedIn !== generation) return;
      inFlight.delete(cartKey);
      // Read again when the wanted read moved while this one was in flight. A failed read that
      // is still the wanted one is retried by a mounted consumer, on its backoff.
      const latest = wantedByCart.get(cartKey);
      if (
        latest !== undefined &&
        (latest.mark !== wanted.mark || latest.refresh !== wanted.refresh)
      ) {
        readOptions(cartKey);
      }
    });
}

/** Bring the cart's cached options up to `mark`: read when none are cached, they were read
 *  under another mark or before the latest refresh, or they expired. */
function syncOptions(cart: CartTarget, mark: string | null): void {
  const cartKey = cartTargetKey(cart);
  const wanted: Wanted = { cart, mark, refresh: wantedByCart.get(cartKey)?.refresh ?? 0 };
  wantedByCart.set(cartKey, wanted);
  if (isCurrent(useFulfilmentOptionsStore.getState().byCart[cartKey], wanted)) return;
  readOptions(cartKey);
}

/**
 * Read the cart's options again (D-2: a refused choice refreshes the calendar). The cached
 * answer stays shown until the new one lands, unless `drop` (the cart no longer offers the
 * choice): then it is forgotten at once, so the card hides and a send carries no fulfilment
 * fields. A read in flight is read again after it lands, as it may predate the refusal.
 */
export function refreshFulfilmentOptions(
  cart: CartTarget,
  { drop = false }: { readonly drop?: boolean } = {},
): void {
  const cartKey = cartTargetKey(cart);
  const previous = wantedByCart.get(cartKey);
  wantedByCart.set(cartKey, {
    cart,
    mark: previous?.mark ?? null,
    refresh: (previous?.refresh ?? 0) + 1,
  });
  if (drop) {
    useFulfilmentOptionsStore.setState((state) => ({ byCart: without(state.byCart, cartKey) }));
  }
  readOptions(cartKey);
}

/** True when the cart's cached options offer the choice: a send of it may then carry
 *  fulfilment fields. False when they do not, or none are cached (unread, or the read failed,
 *  D-15). */
export function fulfilmentOfferedFor(cartKey: string): boolean {
  return useFulfilmentOptionsStore.getState().byCart[cartKey]?.options.offered === true;
}

/** Enabler E8-G (ADO-3109), AC16: the delivery dates the cart's cached options offer, `[]` when
 *  they offer no choice, or `null` while none are cached (unread, or the read failed). */
export function offeredDeliveryDatesFor(cartKey: string): readonly string[] | null {
  const options = useFulfilmentOptionsStore.getState().byCart[cartKey]?.options;
  if (options === undefined) return null;
  return options.offered ? options.deliveryDates : [];
}

/** Enabler E8-G (ADO-3109), AC16: call `listener` whenever a cart's cached options change. Returns
 *  the unsubscribe. */
export function subscribeToFulfilmentOptions(listener: () => void): () => void {
  return useFulfilmentOptionsStore.subscribe((state, previous) => {
    if (state.byCart !== previous.byCart) listener();
  });
}

/** Read the cart's options again unless its cached answer is still what is wanted. */
function readIfStale(cartKey: string): void {
  const wanted = wantedByCart.get(cartKey);
  if (wanted === undefined) return;
  if (isCurrent(useFulfilmentOptionsStore.getState().byCart[cartKey], wanted)) return;
  readOptions(cartKey);
}

export function useFulfilmentOptions(
  cart: CartTarget | null,
  mark: string | null,
): FulfilmentOptions | null {
  const cartKey = cart === null ? null : cartTargetKey(cart);
  // A new object for the same cart is the same read: the effect re-runs on the key alone.
  const cartRef = useRef(cart);
  cartRef.current = cart;
  useEffect(() => {
    if (cartKey !== null && cartRef.current !== null) syncOptions(cartRef.current, mark);
  }, [cartKey, mark]);
  // Read again when the shown answer expires. Each answer that lands schedules its own expiry
  // (one already expired, read across the half hour, reads again at once). After a failed read
  // of an expired or missing answer, the retry is scheduled instead, on its backoff. An unmount
  // or a switch of cart clears either.
  const cached = useFulfilmentOptionsStore((state) =>
    cartKey === null ? undefined : state.byCart[cartKey],
  );
  const failed = useFulfilmentOptionsStore((state) =>
    cartKey === null ? undefined : state.failedByCart[cartKey],
  );
  useEffect(() => {
    if (cartKey === null) return;
    // Retrying when the last read failed while the answer was expired or missing.
    const retrying =
      failed !== undefined && (cached === undefined || failed.at >= cached.expiresAt);
    const at = retrying ? retryAt(failed) : (cached?.expiresAt ?? null);
    if (at === null) return;
    const timer = setTimeout(() => readIfStale(cartKey), Math.max(0, at - Date.now()));
    return () => clearTimeout(timer);
  }, [cartKey, cached, failed]);
  // A tab back in view or a device back online reads an expired answer at once (a sleeping
  // tablet's timers may not have run).
  useEffect(() => {
    if (cartKey === null) return;
    const recheck = () => {
      if (document.visibilityState === 'visible') readIfStale(cartKey);
    };
    document.addEventListener('visibilitychange', recheck);
    window.addEventListener('online', recheck);
    return () => {
      document.removeEventListener('visibilitychange', recheck);
      window.removeEventListener('online', recheck);
    };
  }, [cartKey]);
  return cached?.options ?? null;
}

/** Forget every cached answer (tests; the suite runs with `isolate: false`). */
export function resetFulfilmentOptions(): void {
  generation += 1;
  inFlight.clear();
  wantedByCart.clear();
  useFulfilmentOptionsStore.setState({ byCart: {}, failedByCart: {} });
}
